import { FundingRound, type Headcount } from "@prisma/client";
import HttpStatus from "http-status-codes";
import { type NextApiHandler } from "next";
import { array, boolean, mixed, number, object, ref, string } from "yup";
import type Reference from "yup/lib/Reference";
import { type ApiRoute } from "~/hooks/useApi";
import { api } from "~/lib/api";
import { createMutation, invalidateQuery } from "~/lib/reactQuery";
import { type YupOutputType } from "~/lib/utils";
import { FETCH_FILTERS_PRESETS_QUERY_KEY } from "~/pages/api/filters-preset/fetch-filters-presets";
import { EmployeeLevel } from "~/services/employee/employeeLevel";
import { type CreateFiltersPresetResult, createFiltersPreset } from "~/services/filters-preset/createFiltersPreset";

const FiltersSchema = object({
  fundingRounds: array()
    .of(mixed<FundingRound>().oneOf(Object.values(FundingRound)).nullable())
    .ensure(),
  headcount: mixed<Headcount>().nullable(),
  industriesIds: array().of(number().nullable()).ensure(),
  industryId: number().nullable(),
  jobsIds: array().of(string().nullable()),
  labelsIds: array().of(number().nullable()).ensure(),
  locationsIds: array().of(number().nullable()).ensure(),
  size: object({
    min: number().min(0).nullable(),
    max: number().min(1).nullable(),
  }).required(),
});

export const CreatePresetSchema = object({
  name: string().required(),
  levels: array().of(mixed<EmployeeLevel>().oneOf(Object.values(EmployeeLevel)).nullable()),
  mergeAdvancedLevels: boolean().required(),
  market: FiltersSchema.nullable(),
  percentiles: object({
    low: number().min(0.1).required(),
    high: number().max(0.9).required(),
    middle: number()
      .required()
      .min(ref("low") as Reference<number>)
      .max(ref("high") as Reference<number>),
  }).required(),
}).noUnknown();

export type CreatePresetInput = YupOutputType<typeof CreatePresetSchema>;

const handler: NextApiHandler<CreateFiltersPresetResult> = async (req, res) => {
  const input = CreatePresetSchema.validateSync(req.body, { abortEarly: false });

  const preset = await createFiltersPreset(req, input);

  return res.status(HttpStatus.OK).json(preset);
};

export default api(handler, {
  method: "POST",
  authentication: {
    canAccessMarketData: true,
  },
});

export const CREATE_PRESET_QUERY_KEY: ApiRoute["pathname"] = "/api/filters-preset/create-preset";

export const useCreatePresetMutation = createMutation<typeof handler, typeof CreatePresetSchema>({
  path: CREATE_PRESET_QUERY_KEY,
  schema: CreatePresetSchema,
  options: ({ queryClient }) => ({
    onSuccess: async () => {
      await invalidateQuery(queryClient, FETCH_FILTERS_PRESETS_QUERY_KEY);
    },
  }),
});
